/**
 * Capability matrix keyed [GameType][GameStatus] → capabilities.
 *
 * One source of truth that backend gating, frontend transition UI, and overlay
 * rendering all consume. Adding a new game = define its lifecycle + this matrix;
 * no other code needs to change.
 *
 * Capability domains:
 *   - adminActions: what an admin can do (gates buttons + handler entry points)
 *   - roundActions: what round handlers may do
 *   - overlayVisibility: which overlays should render
 *   - automation: triggers fired on entering this status
 */
import { GameType } from '../constants/gameTypes';
const EMPTY = {
    adminActions: {},
    roundActions: {},
    overlayVisibility: {},
    automation: {},
};
// --- FPR ---------------------------------------------------------------------
const FPR_CAPABILITIES = {
    draft: {
        adminActions: { canOpenRegistration: true, canStartGame: true, canAbandonGame: true, canDeleteRound: true },
        roundActions: {},
        overlayVisibility: {},
        automation: {},
    },
    pre_registration: {
        adminActions: { canCloseRegistration: true, canStartGame: true, canAbandonGame: true, canDeleteRound: true },
        roundActions: {},
        overlayVisibility: {},
        automation: {},
        playerActions: { acceptsRegistration: true },
    },
    pool_creation: {
        adminActions: { canManagePools: true, canEditTeamMembers: true, canAbandonGame: true, canDeleteRound: true },
        roundActions: {},
        overlayVisibility: {},
        automation: {},
    },
    team_creation: {
        adminActions: { canEditTeamMembers: true, canConfigureDraft: true, canAbandonGame: true, canDeleteRound: true },
        roundActions: {},
        overlayVisibility: {},
        automation: {},
    },
    pre_game: {
        adminActions: { canAbandonGame: true, canDeleteRound: true },
        roundActions: {},
        overlayVisibility: { gameInfo: true, teams: true },
        automation: { onEnter: ['create_team_channels'] },
    },
    lobby: {
        adminActions: { canLoadRound: true, canStartRound: true, canEndGame: true, canAbandonGame: true, canEditCumulativeScores: true, canDeleteRound: true },
        roundActions: { canCreateRound: true, canStartRound: true },
        overlayVisibility: {
            gameInfo: true,
            teams: true,
            cumulativeScores: true,
            leaderboard: true,
            lobbyCard: true,
            notifications: true,
            timers: false,
            roundInfo: false,
        },
        automation: { onEnter: ['lock_teams', 'set_started_at'] },
    },
    active: {
        adminActions: { canEndRound: true, canAbandonGame: true },
        roundActions: { canTransitionRoundStatus: true },
        overlayVisibility: {
            gameInfo: true,
            teams: true,
            timers: true,
            roundInfo: true,
            notifications: true,
            cumulativeScores: false,
            lobbyCard: false,
        },
        automation: { onEnter: ['set_started_at'] },
        playerActions: { acceptsRegistration: true },
    },
    round_ending: {
        adminActions: { canEndGame: true, canAbandonGame: true },
        roundActions: {},
        overlayVisibility: {
            gameInfo: true,
            teams: true,
            timers: false,
            roundInfo: false,
            notifications: true,
            scoresheet: true,
            cumulativeScores: true,
        },
        automation: {},
    },
    completed: {
        adminActions: {},
        roundActions: {},
        overlayVisibility: { gameInfo: true, teams: true, cumulativeScores: true, leaderboard: true },
        automation: { onEnter: ['cleanup_channels', 'evict_cache', 'set_ended_at'] },
    },
    abandoned: {
        adminActions: {},
        roundActions: {},
        overlayVisibility: {},
        automation: { onEnter: ['cleanup_channels', 'evict_cache', 'set_ended_at'] },
    },
};
// --- HNS ---------------------------------------------------------------------
const HNS_CAPABILITIES = {
    draft: {
        adminActions: { canOpenRegistration: true, canStartGame: true, canAbandonGame: true, canDeleteRound: true },
        roundActions: {},
        overlayVisibility: {},
        automation: {},
    },
    pre_registration: {
        adminActions: { canCloseRegistration: true, canStartGame: true, canAbandonGame: true, canDeleteRound: true },
        roundActions: {},
        overlayVisibility: {},
        automation: {},
        playerActions: { acceptsRegistration: true },
    },
    lobby: {
        adminActions: { canLoadRound: true, canStartRound: true, canEndGame: true, canAbandonGame: true, canEditCumulativeScores: true, canDeleteRound: true },
        roundActions: { canCreateRound: true, canStartRound: true },
        overlayVisibility: {
            gameInfo: true,
            players: true,
            cumulativeScores: true,
            leaderboard: true,
            lobbyCard: true,
            notifications: true,
            timers: false,
            roundInfo: false,
        },
        automation: { onEnter: ['set_started_at'] },
    },
    active: {
        adminActions: { canEndRound: true, canAbandonGame: true },
        roundActions: { canTransitionRoundStatus: true },
        overlayVisibility: {
            gameInfo: true,
            players: true,
            timers: true,
            roundInfo: true,
            marcoPolo: true,
            notifications: true,
            cumulativeScores: false,
            lobbyCard: false,
        },
        automation: { onEnter: ['set_started_at'] },
        playerActions: { acceptsRegistration: true },
    },
    round_ending: {
        adminActions: { canEndGame: true, canAbandonGame: true },
        roundActions: {},
        overlayVisibility: {
            gameInfo: true,
            players: true,
            timers: false,
            roundInfo: false,
            notifications: true,
            scoresheet: true,
            cumulativeScores: true,
        },
        automation: {},
    },
    completed: {
        adminActions: {},
        roundActions: {},
        overlayVisibility: { gameInfo: true, players: true, cumulativeScores: true, leaderboard: true },
        automation: { onEnter: ['cleanup_channels', 'evict_cache', 'set_ended_at'] },
    },
    abandoned: {
        adminActions: {},
        roundActions: {},
        overlayVisibility: {},
        automation: { onEnter: ['cleanup_channels', 'evict_cache', 'set_ended_at'] },
    },
};
// --- TRL ---------------------------------------------------------------------
const TRL_CAPABILITIES = {
    draft: {
        adminActions: { canOpenRegistration: true, canStartGame: true, canAbandonGame: true, canDeleteRound: true, canEditMap: true },
        roundActions: {},
        overlayVisibility: {},
        automation: {},
    },
    pre_registration: {
        adminActions: { canCloseRegistration: true, canStartGame: true, canAbandonGame: true, canDeleteRound: true, canEditMap: true },
        roundActions: {},
        overlayVisibility: {},
        automation: {},
        playerActions: { acceptsRegistration: true },
    },
    lobby: {
        adminActions: {
            canLoadRound: true,
            canStartRound: true,
            canStartGame: true,
            canEndGame: true,
            canAbandonGame: true,
            canEditCumulativeScores: true,
            canDeleteRound: true,
        },
        roundActions: { canCreateRound: true, canStartRound: true },
        overlayVisibility: {
            gameInfo: true,
            teams: true,
            cumulativeScores: true,
            leaderboard: true,
            lobbyCard: true,
            notifications: true,
            timers: false,
            roundInfo: false,
            mapTab: true,
        },
        automation: {},
    },
    active: {
        adminActions: { canEndRound: true, canAbandonGame: true },
        roundActions: { canTransitionRoundStatus: true },
        overlayVisibility: {
            gameInfo: true,
            teams: true,
            timers: true,
            roundInfo: true,
            notifications: true,
            cumulativeScores: false,
            lobbyCard: false,
            mapTab: true,
        },
        // Team voice channels are created on the first round start (explicit trigger
        // in gameHandlers, mirroring FPR); declared here for documentation.
        automation: { onEnter: ['set_started_at', 'create_team_channels'] },
        playerActions: { canJoinAsNewPlayer: false, acceptsRegistration: true },
    },
    round_ending: {
        adminActions: { canEndGame: true, canAbandonGame: true },
        roundActions: {},
        overlayVisibility: {
            gameInfo: true,
            teams: true,
            timers: false,
            roundInfo: false,
            notifications: true,
            scoresheet: true,
            cumulativeScores: true,
            mapTab: true,
        },
        automation: {},
        playerActions: { canJoinAsNewPlayer: false },
    },
    completed: {
        adminActions: {},
        roundActions: {},
        overlayVisibility: { gameInfo: true, teams: true, cumulativeScores: true, leaderboard: true, mapTab: true },
        automation: { onEnter: ['cleanup_channels', 'evict_cache', 'set_ended_at'] },
    },
    abandoned: {
        adminActions: {},
        roundActions: {},
        overlayVisibility: {},
        automation: { onEnter: ['cleanup_channels', 'evict_cache', 'set_ended_at'] },
    },
};
// --- HNST --------------------------------------------------------------------
// Rounds can be set up from the start, before the teams exist: each round's
// hiding team and players are decided when it starts (in the lobby).
const HNST_CAPABILITIES = {
    draft: {
        adminActions: { canOpenRegistration: true, canAbandonGame: true, canDeleteRound: true },
        roundActions: { canCreateRound: true },
        overlayVisibility: {},
        automation: {},
    },
    pre_registration: {
        adminActions: { canCloseRegistration: true, canAbandonGame: true, canDeleteRound: true },
        roundActions: { canCreateRound: true },
        overlayVisibility: {},
        automation: {},
        playerActions: { acceptsRegistration: true },
    },
    team_creation: {
        adminActions: { canEditTeamMembers: true, canConfigureDraft: true, canAbandonGame: true, canDeleteRound: true },
        roundActions: { canCreateRound: true },
        overlayVisibility: { gameInfo: true, teams: true },
        automation: {},
    },
    lobby: {
        // Late joiners can still be put on a team.
        adminActions: {
            canEditTeamMembers: true,
            canLoadRound: true,
            canStartRound: true,
            canEndGame: true,
            canAbandonGame: true,
            canEditCumulativeScores: true,
            canDeleteRound: true,
        },
        roundActions: { canCreateRound: true, canStartRound: true },
        overlayVisibility: {
            gameInfo: true,
            players: true,
            teams: true,
            cumulativeScores: true,
            leaderboard: true,
            lobbyCard: true,
            notifications: true,
            timers: false,
            roundInfo: false,
        },
        automation: { onEnter: ['lock_teams', 'create_team_channels', 'set_started_at'] },
    },
    active: {
        adminActions: { canEndRound: true, canAbandonGame: true },
        roundActions: { canCreateRound: true, canTransitionRoundStatus: true },
        overlayVisibility: {
            gameInfo: true,
            players: true,
            teams: true,
            timers: true,
            roundInfo: true,
            marcoPolo: true,
            notifications: true,
            cumulativeScores: false,
            lobbyCard: false,
        },
        automation: { onEnter: ['set_started_at'] },
        // No sign-ups mid-game: the teams are drafted from them days ahead, and
        // anyone who turns up on the night just joins in the client.
    },
    round_ending: {
        adminActions: { canEndGame: true, canAbandonGame: true },
        roundActions: { canCreateRound: true },
        overlayVisibility: {
            gameInfo: true,
            players: true,
            teams: true,
            timers: false,
            roundInfo: false,
            notifications: true,
            scoresheet: true,
            cumulativeScores: true,
        },
        automation: {},
    },
    completed: {
        adminActions: {},
        roundActions: {},
        overlayVisibility: { gameInfo: true, players: true, teams: true, cumulativeScores: true, leaderboard: true },
        automation: { onEnter: ['cleanup_channels', 'evict_cache', 'set_ended_at'] },
    },
    abandoned: {
        adminActions: {},
        roundActions: {},
        overlayVisibility: {},
        automation: { onEnter: ['cleanup_channels', 'evict_cache', 'set_ended_at'] },
    },
};
export const GAME_CAPABILITIES = {
    [GameType.FP_RACE]: FPR_CAPABILITIES,
    [GameType.HIDE_AND_SEEK]: HNS_CAPABILITIES,
    [GameType.TRAILBLAZERS]: TRL_CAPABILITIES,
    [GameType.HIDE_AND_SEEK_TEAMS]: HNST_CAPABILITIES,
};
export function getCapabilities(gameType, status) {
    return GAME_CAPABILITIES[gameType]?.[status] ?? EMPTY;
}
export function canAdminDo(gameType, status, action) {
    return getCapabilities(gameType, status).adminActions[action] === true;
}
export function canRoundDo(gameType, status, action) {
    return getCapabilities(gameType, status).roundActions[action] === true;
}
export function shouldShowOverlay(gameType, status, overlay) {
    return getCapabilities(gameType, status).overlayVisibility[overlay] === true;
}
export function getOnEnterAutomation(gameType, status) {
    return getCapabilities(gameType, status).automation.onEnter ?? [];
}
/**
 * Whether a brand-new player (unknown playerId) may join at this status.
 * Default-allow: only a status that explicitly sets `canJoinAsNewPlayer: false`
 * closes the roster. Callers should still gate terminal statuses separately.
 */
export function canJoinAsNewPlayer(gameType, status) {
    return getCapabilities(gameType, status).playerActions?.canJoinAsNewPlayer !== false;
}
/**
 * Whether the game is currently accepting registrations (Discord pre-reg and
 * mid-game sign-ups). Default-deny: only statuses that opt in are accepting.
 */
export function isRegistrationAccepting(gameType, status) {
    return getCapabilities(gameType, status).playerActions?.acceptsRegistration === true;
}
